import { motion } from "framer-motion";

import type { Ref, RefObject } from "react";

import type { SmileSimulationComparisonBindings } from "../hooks/useSmileSimulationComparison";
import { getSmileSimulationCopy } from "../model/i18n";
import type {
  CameraStatus,
  ResolvedCameraFacingMode,
  SimulationStatus,
  SmileSimulationFrameSize,
  SmileSimulationLanguage,
  SmileSimulationStep,
} from "../model/types";
import { ComparisonSlider } from "./ComparisonSlider";
import { SimulationProgressOverlay } from "./SimulationProgressOverlay";
import { SmileSimulationCaptureGuide } from "./SmileSimulationCaptureGuide";
import { SmileSimulationLiveCamera } from "./SmileSimulationLiveCamera";

type SmileSimulationViewportProps = {
  camera: {
    activeFacingMode: ResolvedCameraFacingMode;
    frameSize: SmileSimulationFrameSize | null;
    message: string;
    status: CameraStatus;
  };
  capture: {
    image: string | null;
  };
  comparison: {
    bindings: SmileSimulationComparisonBindings;
    containerRef: RefObject<HTMLDivElement | null>;
    value: number;
  };
  isDrawingEnabled?: boolean;
  isFrameFixed?: boolean;
  progressVariant?: "default" | "experience";
  language: SmileSimulationLanguage;
  media: {
    cameraFrameViewportRef: Ref<HTMLDivElement>;
    startCameraStream: () => Promise<void>;
    stopCameraStream: () => void;
    videoRef: RefObject<HTMLVideoElement | null>;
  };
  step: SmileSimulationStep;
  simulation: {
    hasComparisonResult: boolean;
    isProcessing: boolean;
    message: string;
    progress: number;
    progressLabel: string;
    progressOffset: number;
    resultImage: string | null;
    status: SimulationStatus;
  };
};

export function SmileSimulationViewport({
  camera,
  capture,
  comparison,
  isDrawingEnabled = false,
  isFrameFixed = false,
  progressVariant = "default",
  language,
  media,
  step,
  simulation,
}: SmileSimulationViewportProps) {
  const copy = getSmileSimulationCopy(language);

  return (
    <div
      ref={media.cameraFrameViewportRef}
      className={`flex min-h-0 w-full items-center justify-center ${
        isFrameFixed ? "h-full flex-none" : "flex-1"
      }`}
    >
      <div
        className="flex w-full max-w-full shrink-0 overflow-hidden rounded-[2rem] bg-[#13071f]"
        style={
          isFrameFixed
            ? {
                width: "100%",
                height: "100%",
              }
            : camera.frameSize
              ? {
                  width: camera.frameSize.width,
                  height: camera.frameSize.height,
                }
              : { aspectRatio: "3 / 4" }
        }
      >
        <div className="relative h-full w-full overflow-hidden bg-[#13071f]">
          {simulation.hasComparisonResult &&
          capture.image &&
          simulation.resultImage ? (
            <ComparisonSlider
              comparison={comparison}
              isDrawingEnabled={isDrawingEnabled}
              images={{
                after: simulation.resultImage,
                before: capture.image,
              }}
            />
          ) : capture.image ? (
            <>
              <img
                src={capture.image}
                alt="Captured smile preview"
                className="h-full w-full object-cover"
              />
              {(simulation.isProcessing || simulation.status === "error") && (
                <SimulationProgressOverlay
                  isSimulationProcessing={simulation.isProcessing}
                  simulationMessage={simulation.message}
                  simulationProgress={simulation.progress}
                  simulationProgressLabel={simulation.progressLabel}
                  simulationProgressOffset={simulation.progressOffset}
                  variant={progressVariant}
                />
              )}
            </>
          ) : step === "capture" ? (
            <>
              <SmileSimulationLiveCamera
                fallbackMessage={copy.capture.cameraLoading}
                message={camera.message}
                onStartCamera={media.startCameraStream}
                onStopCamera={media.stopCameraStream}
                shouldMirror={camera.activeFacingMode !== "environment"}
                status={camera.status}
                videoRef={media.videoRef}
              />
              {camera.status === "ready" && (
                <SmileSimulationCaptureGuide message={copy.capture.guide} />
              )}
            </>
          ) : (
            <motion.div
              className="absolute inset-0 bg-[#13071f]"
              initial={false}
              animate={{ opacity: 1 }}
            />
          )}
        </div>
      </div>
    </div>
  );
}
